import React, { useState, useEffect, useRef, useMemo } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import {
  Award,
  UserCheck,
  Clock,
  Briefcase,
  Play,
  Pause,
  Check,
  CheckCircle2,
  X,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Lock,
  ShieldCheck,
  Star,
  Menu,
  Phone,
  Mail,
  BookOpen,
  Sparkles,
  Volume2,
  VolumeX
} from "lucide-react";

// --- STATE STORAGE HELPER ---
const saveStudentSession = (data) => {
  localStorage.setItem("wealthmaps_student_session", JSON.stringify(data));
};

const getStudentSession = () => {
  try {
    const data = localStorage.getItem("wealthmaps_student_session");
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.error("Failed to parse student session", e);
    return null;
  }
};

// --- HEADER NAVBAR COMPONENT ---
function HeaderNavbar({ onEnrollClick }) {
  return (
    <header className="sticky top-0 z-40 w-full glass-panel border-b border-slate-800/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <div 
          data-testid="header-brand-logo"
          className="flex items-center space-x-3 cursor-pointer select-none"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
            <span className="font-extrabold text-slate-950 text-xl tracking-tighter">WM</span>
          </div>
          <div className="flex flex-col">
            <span className="font-extrabold text-lg sm:text-xl tracking-tight text-white">WealthMaps</span>
            <span className="text-[10px] uppercase font-bold tracking-widest text-amber-500">Sales Academy</span>
          </div>
        </div>

        <nav className="hidden md:flex items-center space-x-8 text-sm font-medium text-slate-300">
          <a href="#benefits" className="hover:text-amber-400 btn-transition">Benefits</a>
          <a href="#curriculum" className="hover:text-amber-400 btn-transition">Curriculum</a>
          <a href="#testimonials" className="hover:text-amber-400 btn-transition">Testimonials</a>
          <a href="#pricing" className="hover:text-amber-400 btn-transition">Pricing</a>
          <a href="#faq" className="hover:text-amber-400 btn-transition">FAQs</a>
        </nav>

        <div>
          <button
            onClick={onEnrollClick}
            className="px-5 py-2.5 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-sm shadow-md hover:shadow-lg hover:shadow-amber-500/10 btn-transition flex items-center space-x-1"
          >
            <span>Enroll Now</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}

// --- PROMO VIDEO SECTION ---
function PromoVideoSection() {
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(24); // mock starting play position
  const [elapsedTime, setElapsedTime] = useState(38); // elapsed time in seconds

  useEffect(() => {
    let interval;
    if (isOpen && isPlaying) {
      interval = setInterval(() => {
        setElapsedTime((prev) => {
          if (prev >= 165) {
            setIsPlaying(false);
            return 165;
          }
          const next = prev + 1;
          setProgress(Math.floor((next / 165) * 100));
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isOpen, isPlaying]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setIsPlaying(false);
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handlePlayToggle = (e) => {
    e.stopPropagation();
    setIsPlaying(!isPlaying);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsPlaying(false);
  };

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-[#080C14] to-[#0D1524] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="lg:grid lg:grid-cols-12 lg:gap-12 items-center">
          
          <div className="lg:col-span-5 mb-10 lg:mb-0">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/25 text-amber-500 text-xs font-semibold uppercase tracking-wider mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Meet Your Mentor</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight mb-4">
              Learn Directly from Sunny Rajput
            </h2>
            <p className="text-amber-500 text-sm font-bold uppercase tracking-wider mb-4">
              Senior Real Estate Strategist & WealthMaps Principal Mentor
            </p>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-6">
              Sunny Rajput built a <strong className="text-white">₹50Cr+ real estate sales volume network</strong> in India. He has personally mentored over 1,200 agents, fresh graduates, and corporate switchers to achieve top-tier commission brackets.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              In this practical course, Sunny skips theoretical textbook fluff. You will get the exact scripts, RERA checklists, and client closing pitches he uses every day in real transactions.
            </p>
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                data-testid="promo-video-play-button"
                onClick={() => { setIsOpen(true); setIsPlaying(true); }}
                className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold shadow-md hover:shadow-lg hover:shadow-amber-500/20 btn-transition space-x-2"
              >
                <Play className="w-5 h-5 fill-current" />
                <span>Watch 2-Min Preview</span>
              </button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-800/80 group">
              <img
                src="https://images.unsplash.com/photo-1541447271487-09612b3f49f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwbHV4dXJ5JTIwYXJjaGl0ZWN0dXJlJTIwc2t5c2NyYXBlciUyMGJ1aWxkaW5nfGVufDB8fHx8MTc5MTk3MzYxNnww&ixlib=rb-4.1.0&q=85"
                alt="Real Estate Sales Masterclass Preview"
                className="w-full aspect-video object-cover brightness-[0.6] group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent flex flex-col justify-end p-6 sm:p-10">
                <div className="flex items-center space-x-4 mb-4">
                  <button
                    onClick={() => { setIsOpen(true); setIsPlaying(true); }}
                    className="w-14 h-14 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center shadow-lg hover:scale-110 active:scale-95 transition-all"
                  >
                    <Play className="w-6 h-6 fill-current translate-x-0.5" />
                  </button>
                  <div>
                    <p className="text-white font-extrabold text-sm sm:text-base">Real Estate Sales Masterclass Promo</p>
                    <p className="text-slate-300 text-xs sm:text-sm">Sunny Rajput • 2m 45s</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* VIDEO PREVIEW MODAL */}
      {isOpen && (
        <div 
          onClick={handleClose}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden shadow-2xl flex flex-col"
          >
            {/* Aspect Video wrapper */}
            <div className="relative aspect-video bg-slate-950 flex items-center justify-center overflow-hidden">
              
              {/* Underlying visual backplate representing Sunny Rajput in presentation mode */}
              <div className="absolute inset-0 bg-[#0F172A] flex items-center justify-center opacity-90 pointer-events-none">
                <img
                  src="https://images.unsplash.com/photo-1742981365879-7ad5b7e7e4ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDY2NDF8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjByZWFsJTIwZXN0YXRlJTIwZXhlY3V0aXZlJTIwcHJvZmVzc2lvbmFsJTIwY29ycG9yYXRlJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzkxOTczNjE2fDA&ixlib=rb-4.1.0&q=85"
                  alt="Sunny Rajput Presenting"
                  className="w-full h-full object-cover brightness-[0.35]"
                />
              </div>

              {/* Mock Video Playing Overlay Controls */}
              <div className="absolute inset-0 flex flex-col justify-between p-6 z-10 bg-gradient-to-b from-black/70 via-transparent to-black/90 pointer-events-none">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-xs font-bold text-slate-300 uppercase tracking-widest">LIVE STREAMING PREVIEW</span>
                  </div>
                  <span className="text-xs text-slate-400 bg-slate-900/60 px-2.5 py-1 rounded-full">HD 1080p</span>
                </div>

                <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
                  <button
                    onClick={handlePlayToggle}
                    className="pointer-events-auto w-20 h-20 rounded-full border-4 border-amber-500/20 bg-slate-950/80 flex items-center justify-center hover:scale-105 active:scale-95 transition-all shadow-xl group"
                  >
                    <div className="w-14 h-14 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center relative">
                      {isPlaying ? (
                        <Pause className="w-6 h-6 text-slate-950 fill-current" />
                      ) : (
                        <Play className="w-6 h-6 text-slate-950 fill-current translate-x-0.5" />
                      )}
                    </div>
                  </button>
                  <p className="mt-4 text-white text-base font-extrabold text-center drop-shadow-md">
                    Sunny Rajput Pitching: "Understand the RERA rules..."
                  </p>
                  <p className="text-xs text-slate-300 text-center drop-shadow-md max-w-sm mt-1">
                    Showing Lesson 3 Preview - High-Ticket Closing Scripts
                  </p>
                </div>

                {/* Bottom slider and volume panel */}
                <div className="w-full space-y-4 pointer-events-auto">
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const percentage = Math.floor((clickX / rect.width) * 100);
                      setProgress(percentage);
                      setElapsedTime(Math.floor((percentage / 100) * 165));
                    }}
                    className="relative h-1.5 w-full bg-slate-800 rounded-full cursor-pointer"
                  >
                    <div 
                      className="absolute top-0 left-0 h-full bg-amber-500 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-md border border-amber-500 cursor-grab"
                      style={{ left: `calc(${progress}% - 7px)` }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center space-x-4">
                      <button 
                        onClick={handlePlayToggle}
                        className="text-slate-300 hover:text-amber-500 btn-transition"
                      >
                        {isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current" />}
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
                        className="text-slate-300 hover:text-amber-500 btn-transition"
                      >
                        {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                      </button>
                      <span className="text-xs sm:text-sm text-slate-300 font-mono">
                        {formatTime(elapsedTime)} / 2:45
                      </span>
                    </div>
                    <span className="text-xs text-slate-400">
                      WealthMaps Exclusive Tutorial
                    </span>
                  </div>
                </div>

              </div>

              {/* OUT OF FLOW CLOSE BUTTON TO SECURE Z-INDEX HIGHLIGHT */}
              <button
                data-testid="promo-video-close-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="absolute top-4 right-4 z-40 w-10 h-10 rounded-full bg-slate-900 border border-slate-700 text-white flex items-center justify-center hover:bg-slate-800 hover:border-white shadow-xl transition-all font-bold cursor-pointer"
                title="Close Modal"
              >
                <X className="w-6 h-6 text-slate-100" />
              </button>

            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// --- BENEFIT BLOCKS ---
function BenefitBlocks() {
  const benefits = [
    {
      title: "Industry-Certified Course",
      description: "Gain a recognized WealthMaps certification validating your expertise in Indian real estate sales laws, developer pitching, and client management.",
      icon: Award
    },
    {
      title: "Expert-Led Instruction",
      description: "Direct insights from Sunny Rajput and industry veterans with proven track records in high-ticket residential and commercial deal closings.",
      icon: UserCheck
    },
    {
      title: "Self-Paced & Lifetime Access",
      description: "Self-paced HD video modules, downloadable scripts, legal checklist templates, and 24/7 access to future updates.",
      icon: Clock
    },
    {
      title: "Real-World Projects & Case Studies",
      description: "Hands-on simulations: real site visit roleplays, RERA compliance checks, drafting buyer proposals, and handling objections.",
      icon: Briefcase
    }
  ];

  return (
    <section id="benefits" className="py-20 sm:py-28 bg-[#080C14] border-t border-slate-900/60 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-24">
          <span className="text-amber-500 text-xs font-extrabold uppercase tracking-widest mb-3 block">Why Choose WealthMaps</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
            Designed to Launch Your High-Paying Career
          </h2>
          <p className="mt-4 text-slate-400 text-base sm:text-lg">
            Traditional business colleges skip real-world real estate sales practices. We skip the theory and train you to close transactions on Day 1.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit, idx) => {
            const Icon = benefit.icon;
            return (
              <div
                key={idx}
                data-testid={`benefit-block-item-${idx}`}
                className="glass-card rounded-2xl p-6 sm:p-8 card-hover-scale hover:border-amber-500/30 btn-transition flex flex-col justify-between"
              >
                <div>
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500 mb-6">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 tracking-tight">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-800/60 text-xs text-slate-500 font-bold flex items-center space-x-1">
                  <span>PRACTICAL LEARNING</span>
                  <Check className="w-3.5 h-3.5 text-amber-500" />
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}

// --- CURRICULUM ACCORDION COMPONENT ---
function CurriculumAccordion() {
  const [activeModule, setActiveModule] = useState(0);

  const curriculum = [
    {
      module: "Module 1",
      title: "Foundations of Indian Real Estate Market",
      lessons: [
        "Understanding Builder Tiers & Channel Partner Models",
        "Deciphering RERA (Real Estate Regulatory Authority) Guidelines",
        "What is OC (Occupancy Certificate) & CC (Completion Certificate)?",
        "Property Documentation & Legal Due Diligence Basics"
      ]
    },
    {
      module: "Module 2",
      title: "Lead Generation & Client Prospecting",
      lessons: [
        "Digital Prospecting on LinkedIn & Local Portals",
        "Cold Pitching High Net-Worth Individuals (HNIs)",
        "Building Developer & Channel Partner Alliances",
        "Qualifying Buyers: Budget vs Timeline Analysis"
      ]
    },
    {
      module: "Module 3",
      title: "The Art of High-Ticket Site Visits & Pitching",
      lessons: [
        "Pre-Visit Preparation & Sample Flat Presentation",
        "Highlighting Floor Plans, Carpet Area vs Built-up Area",
        "Addressing RERA Registrations & Title Clearances with Confidence",
        "Mastering the On-Site Closing Technique"
      ]
    },
    {
      module: "Module 4",
      title: "Negotiation, Objections & Closing Deals",
      lessons: [
        "Handling 'Price is Too High' & Competitor Comparisons",
        "Structuring Payment Plans (CLP, 20:80, Down Payment)",
        "Closing Script Walkthrough & Token Amount Collection",
        "Post-Sales Relationship & Referral Generation"
      ]
    }
  ];

  return (
    <section id="curriculum" className="py-20 sm:py-28 bg-gradient-to-b from-[#080C14] to-[#0A0E18] border-t border-slate-900/60">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center mb-16 sm:mb-24">
          <span className="text-amber-500 text-xs font-extrabold uppercase tracking-widest mb-3 block">Complete Syllabus Roadmap</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
            Comprehensive Sales Curriculum
          </h2>
          <p className="mt-4 text-slate-400 text-sm sm:text-base max-w-2xl mx-auto">
            4 deep-dive interactive modules designed to turn a fresher or intern into a professional closer within 4 weeks.
          </p>
        </div>

        <div className="space-y-4">
          {curriculum.map((moduleData, index) => {
            const isExpanded = activeModule === index;
            return (
              <div
                key={index}
                className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden"
              >
                <button
                  data-testid="curriculum-accordion-trigger"
                  onClick={() => setActiveModule(isExpanded ? null : index)}
                  className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-slate-900/60 btn-transition cursor-pointer focus:outline-none"
                >
                  <div className="flex items-center space-x-4">
                    <span className="text-xs font-bold text-amber-500 bg-amber-500/10 border border-amber-500/20 px-3 py-1 rounded-full uppercase tracking-wider">
                      {moduleData.module}
                    </span>
                    <h3 className="text-base sm:text-lg font-bold text-white">
                      {moduleData.title}
                    </h3>
                  </div>
                  <div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-amber-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-800/80 bg-slate-950/40 px-6 py-5 sm:py-6">
                    <ul className="space-y-4">
                      {moduleData.lessons.map((lesson, lessonIdx) => (
                        <li key={lessonIdx} className="flex items-start space-x-3 text-slate-300">
                          <div className="w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500 mt-0.5 shrink-0">
                            <Check className="w-3 h-3" />
                          </div>
                          <span className="text-sm sm:text-base leading-relaxed">{lesson}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}

// --- STUDENT TESTIMONIALS GRID ---
function TestimonialGrid() {
  const testimonials = [
    {
      id: "jyoti",
      name: "Jyoti",
      role: "Fresh Graduate & Junior Sales Executive",
      avatar: "https://images.unsplash.com/photo-1758600587839-56ba05596c69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxJbmRpYW4lMjBwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHNtaWxpbmclMjBwb3J0cmFpdCUyMGNvcnBvcmF0ZXxlbnwwfHx8fDE3OTE5NzM2MjN8MA&ixlib=rb-4.1.0&q=85",
      quote: "Before Sunny Rajput's course, I didn't know how to explain RERA or title reports to buyers. The confidence I gained helped me clear 3 developer interviews and land my first real estate sales job in Pune!",
      outcome: "Hired as Real Estate Advisor at Top Developer"
    },
    {
      id: "manasvi",
      name: "Manasvi",
      role: "Career Switcher from Telecalling",
      avatar: "https://images.unsplash.com/photo-1758518729466-827cd8293992?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxJbmRpYW4lMjBwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHNtaWxpbmclMjBwb3J0cmFpdCUyMGNvcnBvcmF0ZXxlbnwwfHx8fDE3OTE5NzM2MjN8MA&ixlib=rb-4.1.0&q=85",
      quote: "Understanding OC (Occupancy Certificate) and CC (Completion Certificate) properly was a game-changer for me. Clients trust me instantly when I walk them through legal documentation smoothly.",
      outcome: "Closed first ₹85L 2BHK deal in 30 days"
    },
    {
      id: "arpita",
      name: "Arpita",
      role: "Former Retail Associate",
      avatar: "https://images.unsplash.com/photo-1762341124796-530c0085f7d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjBwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHNtaWxpbmclMjBwb3J0cmFpdCUyMGNvcnBvcmF0ZXxlbnwwfHx8fDE3OTE5NzM2MjN8MA&ixlib=rb-4.1.0&q=85",
      quote: "The practical site-visit walkthroughs in Module 3 gave me exact scripts to overcome buyer hesitation. Worth 10x the ₹999 price tag!",
      outcome: "Promoted to Senior Relationship Executive"
    },
    {
      id: "bharti",
      name: "Bharti",
      role: "Final Year MBA Student",
      avatar: "https://images.unsplash.com/photo-1666867936058-de34bfd5b320?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxJbmRpYW4lMjBwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHNtaWxpbmclMjBwb3J0cmFpdCUyMGNvcnBvcmF0ZXxlbnwwfHx8fDE3OTE5NzM2MjN8MA&ixlib=rb-4.1.0&q=85",
      quote: "WealthMaps delivered exactly what colleges skip—real market knowledge on builder payment plans and RERA compliance. I secured a high-paying internship before graduation!",
      outcome: "Landed ₹4.5LPA Real Estate Internship"
    }
  ];

  return (
    <section id="testimonials" className="py-20 sm:py-28 bg-[#080C14] border-t border-slate-900/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-24">
          <span className="text-amber-500 text-xs font-extrabold uppercase tracking-widest mb-3 block">Real Results from Real Students</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
            Our Alums Are Cracking Deal Closings
          </h2>
          <p className="mt-4 text-slate-400 text-base">
            Read inspiring stories of graduates, interns, and professionals who mastered RERA, OC and CC, and landed senior real estate careers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              data-testid={`testimonial-card-${testimonial.id}`}
              className="glass-card rounded-2xl p-6 sm:p-8 border border-slate-800 hover:border-amber-500/20 card-hover-scale btn-transition flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center space-x-1 mb-6 text-amber-500">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-slate-300 text-sm sm:text-base italic leading-relaxed mb-6">
                  "{testimonial.quote}"
                </p>
              </div>

              <div className="flex items-center space-x-4 pt-6 border-t border-slate-800/80">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover border border-slate-700 shrink-0"
                />
                <div>
                  <h4 className="text-white font-bold text-sm sm:text-base">{testimonial.name}</h4>
                  <p className="text-slate-400 text-xs">{testimonial.role}</p>
                  <p className="text-amber-500 text-xs font-bold mt-1 uppercase tracking-wider">{testimonial.outcome}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

// --- COUNTDOWN TIMER COMPONENT ---
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState(14 * 60 + 58);

  useEffect(() => {
    let targetTime = sessionStorage.getItem("wealthmaps_promo_target");
    if (!targetTime) {
      const now = Date.now();
      const end = now + 15 * 60 * 1000; // 15 minutes countdown
      sessionStorage.setItem("wealthmaps_promo_target", end.toString());
      targetTime = end.toString();
    }

    const updateTimer = () => {
      const distance = parseInt(targetTime, 10) - Date.now();
      if (distance <= 0) {
        const nextEnd = Date.now() + 15 * 60 * 1000;
        sessionStorage.setItem("wealthmaps_promo_target", nextEnd.toString());
        setTimeLeft(15 * 60);
      } else {
        setTimeLeft(Math.floor(distance / 1000));
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, []);

  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;

  return (
    <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-red-500/10 border border-red-500/25 text-red-500 text-xs font-semibold uppercase tracking-wider mb-6">
      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
      <span>HURRY! ₹999 PROMO ENDS IN: {mins}m {secs < 10 ? "0" : ""}{secs}s</span>
    </div>
  );
}

// --- PRICING BLOCK ---
function PricingCard({ onEnrollClick }) {
  return (
    <section id="pricing" className="py-20 sm:py-28 bg-gradient-to-b from-[#080C14] to-[#0D1524] border-t border-slate-900/60 relative overflow-hidden">
      
      {/* Background radial highlight */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-500/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
          <span className="text-amber-500 text-xs font-extrabold uppercase tracking-widest mb-3 block">Limited Launch Special</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
            Invest in Your Career Today
          </h2>
          <p className="mt-4 text-slate-400 text-sm sm:text-base">
            No long commitments. Gain high-value real estate sales skills now and earn commissions for life.
          </p>
        </div>

        <div className="glass-card rounded-3xl p-8 sm:p-12 border-2 border-amber-500/30 shadow-2xl relative">
          
          {/* Discount Badge */}
          <div className="absolute -top-4 right-8 bg-amber-500 text-slate-950 font-extrabold text-xs uppercase px-4 py-1.5 rounded-full shadow-lg tracking-wider">
            30% off your first month
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            
            <div className="space-y-6">
              <CountdownTimer />
              <h3 className="text-2xl font-extrabold text-white tracking-tight">
                Real Estate Sales Masterclass
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Unlock instant self-paced lifetime access to the complete 4 modules, trainer-guided sales scripts, standard RERA template sheets, and community forums.
              </p>

              <div className="space-y-3 pt-2">
                {[
                  "Recognized Certificate of Completion",
                  "Direct interactive mentorship Q&As",
                  "Buyer communication scripts sheet",
                  "14-Day Assurance program"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-3 text-slate-300 text-sm font-medium">
                    <div className="w-5 h-5 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500 shrink-0">
                      <Check className="w-3 h-3" />
                    </div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-950/40 border border-slate-800 rounded-2xl p-6 sm:p-8 text-center space-y-6">
              <div>
                <p className="text-slate-400 text-xs uppercase font-extrabold tracking-widest mb-1">Total Course Fee</p>
                <div className="flex items-center justify-center space-x-3">
                  <span className="text-slate-500 text-2xl line-through font-extrabold">₹1,999</span>
                  <span className="text-white text-5xl font-extrabold tracking-tight">₹999</span>
                </div>
                <p className="text-amber-500 text-xs font-bold mt-2">All Inclusive Tax • 100% Lifetime Access</p>
              </div>

              <div className="pt-2 border-t border-slate-800/80">
                <button
                  data-testid="pricing-card-enroll-button"
                  onClick={onEnrollClick}
                  className="w-full py-4 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-extrabold text-base shadow-lg hover:shadow-amber-500/15 active:scale-98 transition-all flex items-center justify-center space-x-2 cursor-pointer"
                >
                  <span>Enroll Now</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center justify-center space-x-1.5 text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider font-semibold">
                <Lock className="w-3.5 h-3.5 text-slate-500" />
                <span>100% Secure Checkout via SSL Encryption</span>
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
}

// --- FAQ SECTION ---
function FAQSection() {
  const [expandedIndex, setExpandedIndex] = useState(0);

  const faqs = [
    {
      question: "Is this course suitable for complete freshers with no real estate experience?",
      answer: "Yes! The course starts from absolute fundamentals—explaining terminology like carpet area, RERA, OC, CC, payment plans, and site visit etiquette step-by-step."
    },
    {
      question: "How long will I have access to the course content?",
      answer: "You receive lifetime unlimited access to all video lectures, course updates, scripts, and downloadable legal templates."
    },
    {
      question: "Will I get an industry certificate upon completion?",
      answer: "Yes, after completing all 4 modules and the practical assessment, you receive a verified WealthMaps Real Estate Sales Certificate."
    },
    {
      question: "What is the offer duration for the ₹999 pricing?",
      answer: "The current ₹999 price (down from ₹1999) includes a 50% launch discount plus a '30% off your first month' bonus voucher for WealthMaps pro community access."
    },
    {
      question: "How do I access the course immediately after payment?",
      answer: "Upon successful payment on the Checkout page, you are redirected to the Thank-You page with your instant access link, login credentials via SMS/Email, and a private WhatsApp community link."
    }
  ];

  return (
    <section id="faq" className="py-20 sm:py-28 bg-[#080C14] border-t border-slate-900/60">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center mb-16 sm:mb-20">
          <span className="text-amber-500 text-xs font-extrabold uppercase tracking-widest mb-3 block">Common Questions</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <div
                key={index}
                data-testid="faq-accordion-item"
                className="rounded-xl border border-slate-800/80 bg-slate-900/20"
              >
                <button
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-900/40 btn-transition cursor-pointer focus:outline-none"
                >
                  <h3 className="text-base sm:text-lg font-bold text-white pr-4">
                    {faq.question}
                  </h3>
                  <div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-amber-500 shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-800/60 bg-slate-950/20 p-5 text-sm sm:text-base text-slate-300 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}

// --- STICKY bottom CTA COMPONENT ---
function StickyCTA({ onEnrollClick }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // reveal CTA bar after scrolling past 450 pixels
      if (window.scrollY > 450) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div
      data-testid="sticky-cta-bar"
      className="fixed bottom-0 left-0 right-0 z-40 bg-slate-950/90 backdrop-blur-lg border-t border-slate-800 p-4 shadow-xl flex items-center justify-between max-w-7xl mx-auto sm:rounded-t-3xl sm:border-x sm:border-slate-800/80"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
        <div>
          <p className="text-white font-extrabold text-sm sm:text-base truncate">Real Estate Sales Masterclass</p>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-slate-400 line-through">₹1,999</span>
            <span className="text-amber-500 text-sm font-extrabold">₹999</span>
            <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 rounded-full uppercase font-bold">30% off month 1</span>
          </div>
        </div>
      </div>

      <button
        data-testid="sticky-cta-enroll-button"
        onClick={onEnrollClick}
        className="px-6 py-2.5 sm:px-8 sm:py-3.5 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-extrabold text-xs sm:text-sm shadow-md hover:shadow-lg hover:shadow-amber-500/15 btn-transition flex items-center space-x-1 shrink-0 cursor-pointer"
      >
        <span>Enroll Now</span>
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// --- HERO SECTION ---
function HeroSection({ onEnrollClick }) {
  return (
    <section className="relative pt-12 sm:pt-20 pb-16 sm:pb-24 bg-gradient-to-b from-[#0B1120] to-[#080C14] overflow-hidden">
      
      {/* Absolute top highlight vector decoration */}
      <div className="absolute top-0 right-0 w-[600px] h-[500px] bg-amber-500/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          <div className="lg:col-span-7 space-y-6 sm:space-y-8">
            <div className="inline-flex items-center space-x-2.5 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Certified Professional Funnel</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1] font-heading">
              Launch a High-Income Career in Real Estate Sales
            </h1>

            <p className="text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed max-w-2xl font-body">
              Learn how to close ₹1Cr+ property deals, master RERA regulations, understand OC & CC documents, and lead site visits like a seasoned top-tier agent.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 pt-2">
              <button
                data-testid="hero-enroll-now-cta"
                onClick={onEnrollClick}
                className="px-8 py-4 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-extrabold text-base sm:text-lg shadow-lg hover:shadow-amber-500/20 active:scale-98 transition-all flex items-center justify-center space-x-2 cursor-pointer"
              >
                <span>Enroll Now for ₹999</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="#curriculum"
                className="px-6 py-4 rounded-full bg-slate-900/60 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 font-bold text-sm sm:text-base transition-all text-center"
              >
                Explore Syllabus
              </a>
            </div>

            <div className="flex items-center space-x-6 text-xs sm:text-sm text-slate-400 pt-4 border-t border-slate-900">
              <div className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-amber-500" />
                <span>Certification Included</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-amber-500" />
                <span>Lifetime Access</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-800/80 group">
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent z-10" />
              <img
                src="https://images.unsplash.com/photo-1742981365879-7ad5b7e7e4ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDY2NDF8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjByZWFsJTIwZXN0YXRlJTIwZXhlY3V0aXZlJTIwcHJvZmVzc2lvbmFsJTIwY29ycG9yYXRlJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzkxOTczNjE2fDA&ixlib=rb-4.1.0&q=85"
                alt="Trainer Sunny Rajput"
                className="w-full aspect-[4/5] object-cover group-hover:scale-105 transition-all duration-700 brightness-[0.7]"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                <p className="text-amber-500 text-xs uppercase font-extrabold tracking-widest mb-1">Academy Instructor</p>
                <h3 className="text-white text-xl font-extrabold tracking-tight">Sunny Rajput</h3>
                <p className="text-slate-300 text-xs">Senior real estate strategist, closed ₹50Cr+ volumes</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

// --- MAIN LANDING PAGE CONTAINER ---
function LandingPage() {
  const navigate = useNavigate();

  const handleEnrollClick = () => {
    navigate("/checkout");
  };

  return (
    <main className="min-h-screen bg-[#080C14] noise-overlay pb-20">
      <HeaderNavbar onEnrollClick={handleEnrollClick} />
      <HeroSection onEnrollClick={handleEnrollClick} />
      <PromoVideoSection />
      <BenefitBlocks />
      <CurriculumAccordion />
      <TestimonialGrid />
      <PricingCard onEnrollClick={handleEnrollClick} />
      <FAQSection />
      <StickyCTA onEnrollClick={handleEnrollClick} />
    </main>
  );
}

// --- CHECKOUT PAGE ---
function CheckoutPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    profession: "fresher"
  });
  const [errors, setErrors] = useState({});
  const [couponCode, setCouponCode] = useState("WEALTH30");
  const [couponApplied, setCouponApplied] = useState(true);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error immediately on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Full Name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email Address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone Number is required";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/[\s-+]/g, ""))) {
      newErrors.phone = "Please enter a valid 10-digit mobile number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      saveStudentSession(formData);
      navigate("/thank-you");
    }
  };

  return (
    <main className="min-h-screen bg-[#080C14] noise-overlay py-12 px-4 sm:px-6 lg:px-8">
      
      {/* Checkout header */}
      <div 
        data-testid="checkout-header"
        className="max-w-5xl mx-auto flex items-center justify-between mb-8 sm:mb-12 border-b border-slate-900 pb-6"
      >
        <div 
          onClick={() => navigate("/")}
          className="flex items-center space-x-2 text-sm text-slate-400 hover:text-white cursor-pointer transition-all"
        >
          <span>← Back to course details</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-amber-500 to-amber-600 flex items-center justify-center shadow">
            <span className="font-extrabold text-slate-950 text-sm">WM</span>
          </div>
          <span className="font-extrabold text-white text-sm uppercase tracking-wider">WealthMaps</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Form area */}
        <div className="lg:col-span-7 space-y-6">
          <div className="glass-card rounded-2xl p-6 sm:p-8 border border-slate-800">
            <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-6 font-heading">
              Student Enrollment Form
            </h2>

            <form onSubmit={handleFormSubmit} className="space-y-5">
              
              <div>
                <label className="block text-slate-300 text-xs uppercase font-extrabold tracking-wider mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
                  data-testid="checkout-student-name-input"
                  className="w-full bg-slate-950 border border-slate-800 focus:border-amber-500 rounded-xl px-4 py-3.5 text-white placeholder-slate-500 text-sm focus:outline-none transition-all"
                />
                {errors.name && (
                  <p className="text-red-500 text-xs font-bold mt-1.5">{errors.name}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 text-xs uppercase font-extrabold tracking-wider mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    data-testid="checkout-student-email-input"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-amber-500 rounded-xl px-4 py-3.5 text-white placeholder-slate-500 text-sm focus:outline-none transition-all"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs font-bold mt-1.5">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-slate-300 text-xs uppercase font-extrabold tracking-wider mb-2">
                    Mobile Number
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="10-digit phone number"
                    data-testid="checkout-student-phone-input"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-amber-500 rounded-xl px-4 py-3.5 text-white placeholder-slate-500 text-sm focus:outline-none transition-all"
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-xs font-bold mt-1.5">{errors.phone}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-slate-300 text-xs uppercase font-extrabold tracking-wider mb-2">
                  Your Current Status
                </label>
                <div className="relative">
                  <select
                    name="profession"
                    value={formData.profession}
                    onChange={handleInputChange}
                    className="w-full bg-slate-950 border border-slate-800 focus:border-amber-500 rounded-xl px-4 py-3.5 text-white text-sm focus:outline-none transition-all appearance-none cursor-pointer pr-10"
                  >
                    <option value="fresher">Fresher Seeking Career</option>
                    <option value="intern">Active Intern</option>
                    <option value="working_pro">Working Sales Professional</option>
                    <option value="other">Other Status</option>
                  </select>
                  <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-slate-400">
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-800/80">
                <p className="text-slate-300 text-xs uppercase font-extrabold tracking-wider mb-4">
                  Select Payment Option
                </p>

                <div className="space-y-3">
                  <label className="flex items-center justify-between p-4 bg-slate-950/40 border border-amber-500/40 rounded-xl cursor-pointer hover:bg-slate-950 transition-all">
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="payment_method"
                        value="upi"
                        defaultChecked
                        data-testid="checkout-upi-payment-radio"
                        className="text-amber-500 focus:ring-amber-500 h-4.5 w-4.5 bg-slate-950 border-slate-800"
                      />
                      <div>
                        <p className="text-white text-sm font-bold">UPI Payments (Instant)</p>
                        <p className="text-slate-400 text-xs">GPay, PhonePe, Paytm, or BHIM</p>
                      </div>
                    </div>
                    <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full uppercase font-bold">FASTEST</span>
                  </label>

                  <label className="flex items-center justify-between p-4 bg-slate-950/20 border border-slate-800 rounded-xl cursor-not-allowed opacity-60">
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="payment_method"
                        value="card"
                        disabled
                        className="text-amber-500 focus:ring-amber-500 h-4.5 w-4.5 cursor-not-allowed"
                      />
                      <div>
                        <p className="text-white text-sm font-bold">Credit or Debit Cards</p>
                        <p className="text-slate-400 text-xs">Visa, Mastercard, RuPay, or AMEX</p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  data-testid="checkout-pay-now-button"
                  className="w-full py-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-950 font-extrabold text-base shadow-lg hover:shadow-amber-500/15 active:scale-98 transition-all flex items-center justify-center space-x-2 cursor-pointer"
                >
                  <Lock className="w-5 h-5" />
                  <span>Secure Pay & Complete Enrollment</span>
                </button>
              </div>

              <div className="flex items-center justify-center space-x-2 text-[11px] text-slate-400">
                <ShieldCheck className="w-4.5 h-4.5 text-emerald-500" />
                <span>100% Authorized & RERA-aligned Sales Coaching Guarantee</span>
              </div>

            </form>
          </div>
        </div>

        {/* Order details area */}
        <div className="lg:col-span-5 space-y-6">
          <div className="glass-card rounded-2xl p-6 sm:p-8 border border-slate-800">
            <h3 className="text-lg font-bold text-white mb-6 uppercase tracking-wider border-b border-slate-800 pb-3">
              Order Summary
            </h3>

            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-white font-bold text-sm">Real Estate Sales Masterclass</h4>
                  <p className="text-slate-400 text-xs mt-1">Instructor: Sunny Rajput • Lifetime access</p>
                </div>
                <span className="text-white text-sm font-extrabold">₹1,999</span>
              </div>

              <div className="flex justify-between text-sm text-slate-400 pt-3 border-t border-slate-800">
                <span>Standard Base Price</span>
                <span>₹1,999</span>
              </div>

              <div className="flex justify-between text-sm text-emerald-400">
                <span>Launch Promo Discount</span>
                <span>-₹1,000</span>
              </div>

              {couponApplied && (
                <div className="flex justify-between items-center text-xs text-amber-500 bg-amber-500/5 border border-amber-500/15 px-3 py-2 rounded-lg">
                  <div className="flex items-center space-x-1">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>Coupon <strong>{couponCode}</strong> applied</span>
                  </div>
                  <span className="font-bold">PROMO DISCOUNT LOCK</span>
                </div>
              )}

              <div className="flex justify-between text-base font-extrabold text-white pt-4 border-t border-slate-800">
                <span>Net Payable Amount</span>
                <span className="text-amber-500 text-xl">₹999</span>
              </div>
            </div>

            <div className="mt-8 bg-slate-950/50 rounded-xl p-4 border border-slate-800 flex items-start space-x-3">
              <Award className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-xs font-extrabold uppercase tracking-wide">Money-Back Assurance</p>
                <p className="text-slate-400 text-[11px] mt-1">
                  We are so confident in our practical RERA and CC training that we offer full assurance. Learn safely!
                </p>
              </div>
            </div>

          </div>
        </div>

      </div>
    </main>
  );
}

// --- THANK YOU PAGE ---
function ThankYouPage() {
  const navigate = useNavigate();
  const session = getStudentSession();
  const studentName = session ? session.name : "Learner";

  // Stabilize transaction ID across refreshes/re-renders
  const transactionId = useMemo(() => {
    return `WM-${Math.floor(100000 + Math.random() * 900000)}`;
  }, []);

  const todayStr = useMemo(() => {
    const today = new Date();
    return today.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  }, []);

  return (
    <main className="min-h-screen bg-[#080C14] noise-overlay py-12 px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
      <div className="max-w-3xl mx-auto w-full glass-card rounded-3xl p-8 sm:p-12 border border-slate-800/80 shadow-2xl relative">
        
        {/* Success check badge decoration */}
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-8 shadow-lg shadow-emerald-500/10">
          <CheckCircle2 className="w-10 h-10" />
        </div>

        <div className="text-center space-y-4 mb-8">
          <p className="text-emerald-400 text-xs font-extrabold uppercase tracking-widest">Enrollment Complete</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight font-heading">
            Welcome to WealthMaps, {studentName}!
          </h1>
          <p className="text-slate-300 text-sm sm:text-base max-w-xl mx-auto">
            Your registration was successful. We have sent your onboarding credentials and purchase receipt to your contact inbox.
          </p>
        </div>

        {/* Order Details box */}
        <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-6 mb-8 text-sm space-y-3">
          <h3 className="text-white text-xs uppercase font-extrabold tracking-widest border-b border-slate-800 pb-2 mb-4">
            Receipt & Purchase Details
          </h3>
          <div className="flex justify-between text-slate-400">
            <span>Reference Transaction ID:</span>
            <span className="font-mono text-white">{transactionId}</span>
          </div>
          <div className="flex justify-between text-slate-400">
            <span>Transaction Date:</span>
            <span className="text-white">{todayStr}</span>
          </div>
          <div className="flex justify-between text-slate-400">
            <span>Enrolled Course:</span>
            <span className="text-white font-bold">Real Estate Sales Masterclass</span>
          </div>
          <div className="flex justify-between text-slate-400">
            <span>Total Fees Paid:</span>
            <span className="text-amber-500 font-extrabold">₹999</span>
          </div>
          <div className="flex justify-between text-slate-400">
            <span>Onboarding Status:</span>
            <span className="text-emerald-400 font-bold uppercase tracking-wider text-[11px] bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20">Active Access</span>
          </div>
        </div>

        {/* Onboarding Roadmap */}
        <div className="space-y-6 mb-10">
          <h3 className="text-white text-sm font-extrabold uppercase tracking-wider text-center sm:text-left">
            Your Next Steps Roadmap
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            
            <div className="bg-slate-900/30 border border-slate-800 p-5 rounded-xl text-center space-y-3">
              <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 flex items-center justify-center font-bold mx-auto text-xs">
                1
              </div>
              <h4 className="text-white text-sm font-bold">Check Credentials</h4>
              <p className="text-slate-400 text-xs leading-relaxed">
                Check your Email/SMS for your secure student dashboard login details.
              </p>
            </div>

            <div className="bg-slate-900/30 border border-slate-800 p-5 rounded-xl text-center space-y-3">
              <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 flex items-center justify-center font-bold mx-auto text-xs">
                2
              </div>
              <h4 className="text-white text-sm font-bold">WhatsApp Community</h4>
              <p className="text-slate-400 text-xs leading-relaxed">
                Join our exclusive private channel to link up with fellow agents & freshers.
              </p>
            </div>

            <div className="bg-slate-900/30 border border-slate-800 p-5 rounded-xl text-center space-y-3">
              <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 flex items-center justify-center font-bold mx-auto text-xs">
                3
              </div>
              <h4 className="text-white text-sm font-bold">Start Learning</h4>
              <p className="text-slate-400 text-xs leading-relaxed">
                Launch the orientation preview and download Sunny's buyer closing pitch.
              </p>
            </div>

          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
          <button
            data-testid="thankyou-access-portal-button"
            onClick={() => alert("Launching Learning Management Portal... Welcome aboard!")}
            className="flex-1 py-4 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-extrabold text-sm sm:text-base shadow-lg hover:shadow-amber-500/15 btn-transition flex items-center justify-center space-x-2 cursor-pointer"
          >
            <BookOpen className="w-5 h-5" />
            <span>Launch Academy Dashboard</span>
          </button>

          <button
            data-testid="thankyou-whatsapp-community-button"
            onClick={() => window.open("https://chat.whatsapp.com/mock-wealthmaps", "_blank")}
            className="flex-1 py-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-sm sm:text-base shadow-lg hover:shadow-emerald-500/15 btn-transition flex items-center justify-center space-x-2 cursor-pointer"
          >
            <Phone className="w-5 h-5" />
            <span>Join Private WhatsApp Community</span>
          </button>
        </div>

        <div className="mt-8 text-center">
          <span 
            onClick={() => navigate("/")}
            className="text-slate-400 hover:text-white text-xs cursor-pointer select-none transition-all underline"
          >
            Return back to Funnel Landing Page
          </span>
        </div>

      </div>
    </main>
  );
}

// --- APP ROUTING BOOTSTRAP ---
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;